import React from 'react'
import GuideLayout from '@/components/help/GuideLayout'
import GuideSection from '@/components/help/GuideSection'
import GuideTip from '@/components/help/GuideTip'
import GuideRelatedLinks from '@/components/help/GuideRelatedLinks'
import type { SharedProps } from '@/types/shared'

export default function DashboardGuide({ currentUser, properties }: Pick<SharedProps, 'currentUser' | 'properties'>) {
  return (
    <GuideLayout
      currentUser={currentUser}
      properties={properties}
      title="Dashboard"
      subtitle="Your at-a-glance overview of property health, sync status, and key metrics."
    >
      <GuideSection title="What the Dashboard Shows">
        <p className="text-sm leading-relaxed text-muted-foreground">
          The dashboard is your <strong className="text-foreground">landing page</strong> — the first
          thing you see when you select a property. It gives you a quick health check without needing
          to dig into the calendar or pricing rules.
        </p>
      </GuideSection>

      <GuideSection title="Pipeline Status">
        <p className="text-sm leading-relaxed text-muted-foreground">
          The top bar shows a <strong className="text-foreground">three-step pipeline strip</strong>:
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Inbound</strong> — last time booking data was synced
            from your PMS. If this is stale (&gt;4 hours), the engine may be working with outdated
            booking information.
          </li>
          <li>
            <strong className="text-foreground">Computation</strong> — last time rates were computed.
            This happens every hour, and again whenever you change pricing rules.
          </li>
          <li>
            <strong className="text-foreground">Push</strong> — last time computed rates were sent to
            your PMS. Green means rates are in sync. Yellow means rates have been computed but not yet
            pushed.
          </li>
        </ul>
      </GuideSection>

      <GuideSection title="Key Metrics">
        <p className="text-sm leading-relaxed text-muted-foreground">
          The dashboard surfaces high-level numbers at a glance:
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Monthly occupancy trend</strong> — how your bookings
            are tracking over the coming weeks
          </li>
          <li>
            <strong className="text-foreground">Rate performance vs base rates</strong> — whether the
            pipeline is adjusting rates up or down on average
          </li>
          <li>
            <strong className="text-foreground">Override count</strong> — how many active overrides
            exist, so you know if manual pricing is in play
          </li>
        </ul>
        <p className="text-sm leading-relaxed text-muted-foreground">
          These are starting points: click through to the calendar or History for details.
        </p>
      </GuideSection>

      <GuideSection title="Quick Actions">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Two actions are available directly from the dashboard:
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Sync from your PMS</strong> — manually trigger a booking
            data refresh
          </li>
          <li>
            <strong className="text-foreground">Recompute rates</strong> — force an immediate rate
            computation (normally happens automatically)
          </li>
        </ul>
        <p className="text-sm leading-relaxed text-muted-foreground">
          These are rarely needed — the system handles syncing and computation on schedule. But they
          are useful after making changes and wanting to see immediate results.
        </p>
      </GuideSection>

      <GuideSection title="Structure Change Alerts">
        <p className="text-sm leading-relaxed text-muted-foreground">
          If room types or rate plans change in your PMS, the dashboard{' '}
          <strong className="text-foreground">alerts you with a warning banner</strong>.
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">New room types</strong> need base rates configured
            before rates can be computed for them
          </li>
          <li>
            <strong className="text-foreground">Removed room types</strong> are flagged so you can
            review pricing relationships and derived room configurations
          </li>
        </ul>
      </GuideSection>

      {/* COMBINED INVENTORY. Said on the page that explains what these numbers mean, because one
          building listed several times changes the SUBJECT of every figure on the dashboard without
          changing any definition. */}
      <GuideSection title="If Your Building Uses Combined Inventory">
        <p className="text-sm leading-relaxed text-muted-foreground">
          When one building is listed several times in your PMS, the listings can be set up as{' '}
          <strong className="text-foreground">combined inventory</strong>, with one of them as the{' '}
          <strong className="text-foreground">lead listing</strong>. At the lead listing, every figure on the
          dashboard describes the <strong className="text-foreground">whole building</strong>: bookings on every
          listing over the building&apos;s rooms. A room sold on two listings (a Same room) is counted once. The
          definitions do not change: occupancy is still rooms sold over rooms available, and ADR is still realized
          revenue over room-nights sold.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          A <strong className="text-foreground">following listing</strong>&apos;s dashboard reports no revenue
          figures, on purpose. It shows what belongs to that listing (its PMS connection, its sync and publishing
          health, its unpublished rates and its rooms) and links to the lead listing for everything else.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Each listing still connects to your PMS and sends its prices on its own. When one of them has a problem,
          such as prices that could not be sent or a connection that needs re-authorising, the warning at the top of
          the lead listing&apos;s pages <strong className="text-foreground">names that listing</strong>, so you see
          it where you work.
        </p>
      </GuideSection>

      {/* The two places the lead's dashboard shows each listing on its own, named. Every other figure is
          the building's, so a figure that is one listing's says whose it is. */}
      <GuideSection title="By Listing" id="by-listing">
        <p className="text-sm leading-relaxed text-muted-foreground">
          Under the pickup table, the <strong className="text-foreground">By listing</strong> card shows how
          the building&apos;s bookings split between its listings, for the same windows as the pickup table.
          Each listing has its own row, by name:
        </p>
        <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
          <li>
            <strong className="text-foreground">Share of building</strong>: the part of the building&apos;s
            room-nights sold in that window that were booked on this listing
          </li>
          <li>
            <strong className="text-foreground">Rooms and revenue on the books</strong>: this listing&apos;s own
            confirmed bookings, revenue gross and spread across the nights of each stay
          </li>
          <li>
            <strong className="text-foreground">ADR</strong>: this listing&apos;s own revenue divided by its own
            room-nights sold
          </li>
          <li>
            <strong className="text-foreground">Pickup 7d</strong>: the rooms this listing gained (or lost to
            cancellations) for that window over the last seven days
          </li>
        </ul>
        <p className="text-sm leading-relaxed text-muted-foreground">
          The <strong className="text-foreground">Building</strong> row is the whole building, the same figures
          as the pickup table, so the listings add up to it. If you have not been given every listing of the
          building, the card is not shown at all: the other listings&apos; bookings still count in every
          building figure on the page, as they always have.
        </p>
      </GuideSection>

      <GuideSection title="Priced At, per Listing" id="priced-at">
        <p className="text-sm leading-relaxed text-muted-foreground">
          <strong className="text-foreground">Priced at</strong> is the list price the engine is asking, not
          money earned. For a combined inventory it is drawn as{' '}
          <strong className="text-foreground">one line per listing</strong>, each the average of that
          listing&apos;s own computed prices across its active rate plans, because listings can sell the same
          rooms at different prices. A following listing whose prices match the lead listing&apos;s on every
          night gets a short note (&ldquo;Same prices as&hellip;&rdquo;) instead of a second, identical line.
        </p>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Rate plans that are switched off in your PMS are left out of Priced at everywhere, for every
          property: the engine sends them nothing, so they are not a price a guest can book.
        </p>
      </GuideSection>

      <GuideSection title="Tips">
        <div className="space-y-3">
          <GuideTip type="do">
            Check the pipeline status strip daily. Green across all three steps means everything is
            healthy.
          </GuideTip>
          <GuideTip type="do">
            Use "Recompute rates" after making pricing rule changes if you want to see the effect
            immediately rather than waiting for the next scheduled run.
          </GuideTip>
          <GuideTip type="dont">
            Do not spam the sync or recompute buttons. The system has rate limits and schedules for a
            reason — frequent manual triggers will not produce different results and may hit your PMS
            provider&apos;s API limits.
          </GuideTip>
        </div>
      </GuideSection>

      <GuideRelatedLinks
        links={[
          { slug: 'how-it-works', title: 'How It Works' },
          { slug: 'rate-calendar', title: 'Rate Calendar' },
          { slug: 'history', title: 'History' },
        ]}
      />
    </GuideLayout>
  )
}
